import React, { FC, useCallback, useMemo } from 'react';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import {
	EventClassDto,
	EventTicketDto,
	FileVariantsEnum,
	formatEventTimeRange,
	getAttendeeUserName,
	OrderDto,
	UserDto,
	makeTicketPageLink,
} from '@sweatpals/shared';
import {
	clsx,
	CoreTooltip,
	InfoIcon,
	Option,
	Select,
	Showable,
	TicketIcon,
	TransferTicketIcon,
	useStyles,
	CalendarEmptyIcon,
	LocationIcon,
} from '@sweatpals/react-ui-kit';
import Link from 'next/link';
import EventAuthors from '../../../event/components/event-authors/event-authors.component';
import { EventType } from '@/modules/_core/types/event-types';
import { CoreButtonBase } from '@/modules/_core/components/_ui/core-button';
import MakeAppQrCode from '@/modules/_core/components/cards/Make-app-qr-code';
import { eventTicketCardStyles } from './event-ticket-card.styles';
import { getMoreExpensivePrices } from '@/modules/_core/utils/getMoreExpensivePrices';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';

enum ManageOptions {
	Cancel = 'cancel',
	Transfer = 'transfer',
	Upgrade = 'upgrade',
}

export interface EventTicketCardProps {
	eventData: EventType;
	order?: OrderDto;
	ticket: Pick<
		EventTicketDto,
		| 'id'
		| 'fullName'
		| 'intakeFormResults'
		| 'eventPrice'
		| 'eventPriceId'
		| 'shortId'
		| 'userId'
		| 'isCanceled'
		| 'isTicketUsed'
	>;
	host?: UserDto;
	coHost?: UserDto;
	eventClasses?: EventClassDto[];
	isNewTicket?: boolean;
	isTicketTransferred?: boolean;
	onCancelTicketButtonClick?(ticketId: string): void;
	onTicketTransferButtonClick?(ticketId: string): void;
	onUpgradeTicketButtonClick?(ticketId: string): void;
}

// eslint-disable-next-line complexity
export const EventTicketCard: FC<EventTicketCardProps> = props => {
	const {
		eventData,
		order,
		ticket,
		host,
		coHost,
		eventClasses,
		isNewTicket,
		onUpgradeTicketButtonClick,
		onTicketTransferButtonClick,
		onCancelTicketButtonClick,
		isTicketTransferred,
	} = props;

	const lgMedia = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
	const styles = useStyles(eventTicketCardStyles);

	const eventBackgroundUrl = useMemo(
		() =>
			eventData?.background &&
			getPublicFileUrl(eventData.background, {
				variant: FileVariantsEnum.S,
			}),
		[eventData?.background]
	);
	const moreExpensivePrices = useMemo(
		() => (eventData.prices && ticket.eventPrice ? getMoreExpensivePrices(eventData.prices, ticket.eventPrice) : []),
		[eventData, ticket]
	);
	const rsvpFullName = useMemo(() => getAttendeeUserName(ticket, order), [ticket, order]);

	const manageOptions = useMemo<Option[]>(() => {
		const opts: Option[] = [];

		// only if ticket is not canceled, not used and not transferred
		if (!ticket.isCanceled && !ticket.isTicketUsed && !isTicketTransferred) {
			if (moreExpensivePrices.length && onUpgradeTicketButtonClick) {
				opts.push({
					value: ManageOptions.Upgrade,
					label: 'Upgrade ticket',
					icon: <TicketIcon style={{ width: '24px', height: '24px' }} />,
				});
			}

			if (onTicketTransferButtonClick) {
				opts.push({
					value: ManageOptions.Transfer,
					label: 'Transfer ticket',
					icon: <TransferTicketIcon style={{ width: '24px', height: '24px' }} />,
				});
			}

			if (onCancelTicketButtonClick) {
				// TODO uncomment when single ticket cancel is implemented, currently only full order cancel is allowed
				// opts.push({ value: ManageOptions.Cancel, label: 'Cancel ticket', icon: <CloseIcon style={{ width: '24px', height: '24px' }} /> });
			}
		}

		return opts;
	}, [
		ticket.isCanceled,
		ticket.isTicketUsed,
		moreExpensivePrices.length,
		isTicketTransferred,
		onTicketTransferButtonClick,
		onUpgradeTicketButtonClick,
		onCancelTicketButtonClick,
	]);

	const onManageOptionChange = useCallback(
		(value: string) => {
			switch (value) {
				case ManageOptions.Cancel: {
					break;
				}
				case ManageOptions.Transfer: {
					onTicketTransferButtonClick?.(ticket.id);
					break;
				}
				case ManageOptions.Upgrade: {
					onUpgradeTicketButtonClick?.(ticket.id);
					break;
				}
			}
		},
		[ticket?.id, onUpgradeTicketButtonClick, onTicketTransferButtonClick]
	);

	return (
		<Box data-component="ticket" sx={styles.ticket}>
			<Showable show={eventBackgroundUrl}>
				<Box sx={styles.ticketBackground}>
					<Box component='img' src={eventBackgroundUrl} alt='event logo' />
				</Box>
			</Showable>
			<Box sx={styles.ticketContent}>
				<Box
					sx={{
						flex: 1,
						px: { xs: 0, lg: '16px' },
						gap: { xs: 0, lg: '16px' },
						display: 'flex',
						overflow: 'hidden',
						flexDirection: { xs: 'column', lg: 'row' },
					}}
				>
					<Box sx={styles.ticketInfo}>
						<Box sx={styles.mainTicketInfo}>
							<Box display='flex' flexDirection='column' gap='14px'>
								<Showable show={lgMedia}>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
										<Typography sx={styles.ticketTitle}>RSVP</Typography>
										<Typography sx={styles.ticketText}>{rsvpFullName}</Typography>
									</Box>
								</Showable>
								<Box display='flex' flexDirection='column' gap='7px'>
									<Typography sx={{ ...styles.ticketTitle, fontWeight: 500, fontSize: '13px' }}>Ticket Type</Typography>
									<Typography sx={{ ...styles.ticketText, fontWeight: 700 }}>{ticket.eventPrice?.tierName}</Typography>
								</Box>
							</Box>
						</Box>
						<Showable show={host}>
							<EventAuthors
								host={host}
								coHost={coHost}
								sx={{
									flex: { xs: 1, lg: 'unset' },
									flexDirection: { xs: 'row', lg: 'column' },
									alignItems: { xs: 'center', lg: 'flex-start' },
									justifyContent: 'flex-end',
								}}
								avatarsSx={{
									height: coHost ? '39px' : '31px',
									width: coHost ? '50px' : '31px',
								}}
								textSx={{
									flex: { xs: 1, lg: 'unset' },
									fontFamily: 'General Sans',
									fontSize: '13px',
									fontWeight: 400,
									lineHeight: '17px',
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
									textOverflow: 'ellipsis',

									'& a': {
										fontWeight: 600,
									},
								}}
								avatarWidth={32}
								avatarHeight={32}
							/>
						</Showable>
					</Box>
					<Box sx={clsx(styles.eventInfo, { pb: { xs: '16px', lg: eventClasses?.length ? '16px' : '12px' } })}>
						<Box gap='12px' display='flex' flexDirection='column' height={!eventClasses?.length ? '100%' : undefined}>
							<Box
								sx={clsx(
									styles.eventName,
									eventClasses?.length
										? {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
										  }
										: undefined
								)}
							>
								{eventData?.name}
							</Box>
							<Box
								sx={clsx(
									{ display: 'flex', flexDirection: 'column', gap: '12px' },
									!eventClasses?.length
										? {
												justifyContent: 'center',
												flex: 1,
										  }
										: undefined
								)}
							>
								<Showable show={!eventData?.additionalData?.hideEventDate}>
									<Box sx={styles.eventInfoItem}>
										<CalendarEmptyIcon width={20} height={20} />
										<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '7px' }}>
											<Typography sx={styles.ticketTitle}>Event Time</Typography>
											<Typography sx={styles.ticketText}>
												{eventData
													? formatEventTimeRange(eventData.startDate, eventData.endDate, {
															timezone: eventData?.tzid,
															startDateFormat: 'eee, LLL dd, p',
															endDateFormat: 'p',
													  })
													: ''}
											</Typography>
										</Box>
									</Box>
								</Showable>
								<Box sx={styles.eventInfoItem}>
									<LocationIcon />
									<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '7px' }}>
										<Typography sx={styles.ticketTitle}>Location</Typography>
										<Typography sx={styles.ticketText}>
											{eventData?.isOnlineEvent ? 'Online event' : eventData?.addressName}
										</Typography>
									</Box>
								</Box>
							</Box>
						</Box>

						<Box
							display='flex'
							alignItems='center'
							gap='20px'
							justifyContent={!eventClasses?.length ? 'flex-end' : 'flex-start'}
						>
							<Showable show={eventClasses?.length}>
								<Box display='flex' alignItems='center' gap='4px'>
									<Link href={`/${makeTicketPageLink({ ticketId: ticket.id, subPath: 'classes' })}`} passHref legacyBehavior>
										<a target='_blank' rel='noopener noreferrer' style={{ textDecoration: 'none', color: 'white' }}>
											<CoreButtonBase sx={styles.ticketSecureSpotButton}>Secure spot</CoreButtonBase>
										</a>
									</Link>
									<CoreTooltip
										arrow
										title={`${
											isNewTicket ? 'We have sent you email. ' : ''
										}Secure your spot by selecting classes you are
										interested in.`}
									>
										<Box>
											<InfoIcon
												width={24} 
												height={24}
												style={{
													color: 'white',
													cursor: 'pointer',
												}}
											/>
										</Box>
									</CoreTooltip>
								</Box>
							</Showable>

							<Showable show={manageOptions.length}>
								{manageOptions?.length > 1 ? (
									<Select
										options={manageOptions}
										value={[]}
										placeholder='Manage ticket'
										onChange={e => {
											onManageOptionChange(e.target.value as string);
										}}
										SelectDisplayProps={{
											style: { paddingRight: 36 },
										}}
										sx={styles.manageTicketSelect}
										MenuProps={{
											PaperProps: {
												sx: styles.manageTicketSelectPaper,
											}
										}}
										MenuItemProps={{
											sx: styles.manageTicketSelectItem,
											textSx: styles.manageTicketSelectItemText,
										}}
									/>
								) : manageOptions?.[0] ? (
									<CoreButtonBase
										sx={styles.upgradeTicketButton}
										onClick={() => {
											onManageOptionChange(manageOptions[0].value);
										}}
									>
										{manageOptions[0].icon}
										{manageOptions[0].label}
									</CoreButtonBase>
								) : null}
							</Showable>
						</Box>
					</Box>
				</Box>
				<Box sx={styles.ticketQrCode(isTicketTransferred, ticket?.fullName)}>
					<Typography sx={styles.ticketIdText}>Ticket ID: {ticket.shortId}</Typography>
					<MakeAppQrCode
						link={ticket.id}
						hideCopyLinkButton
						size={180}
						rootSx={{
							height: '180px',
							width: '180px',
						}}
						qrCodeWrapperSx={{
							backgroundColor: 'transparent',
							boxShadow: 'unset',
							borderRadius: 0,
							p: 0,
						}}
						qrCodeSx={{
							backgroundColor: 'transparent',
							borderRadius: 0,
							p: 0,
						}}
						qrCodeOptions={{
							dotsOptions: {
								color: '#FFFFFF',
							},
							backgroundOptions: {
								color: 'transparent',
							},
							cornersSquareOptions: {
								color: '#FFFFFF',
							},
						}}
					/>
					<Typography sx={styles.ticketQrCodeText}>Scan your ticket during entrance</Typography>
				</Box>
			</Box>
			<Box sx={styles.ticketBorder}>
				<svg>
					<clipPath id='ticket-border-path'>
						<path d='M16.5 -1C7.11116 -1 -0.5 6.61116 -0.5 16H1.5C1.5 7.71573 8.21573 1 16.5 1V-1ZM564.5 -1H16.5V1H564.5V-1ZM581.5 16C581.5 6.61116 573.889 -1 564.5 -1V1C572.784 1 579.5 7.71573 579.5 16H581.5ZM586.5 23H590.501V21H586.5V23ZM612.501 -1C603.112 -1 595.501 6.61116 595.501 16H597.501C597.501 7.71573 604.217 1 612.501 1V-1ZM870.501 -1H612.501V1H870.501V-1ZM887.501 16C887.501 6.61116 879.89 -1 870.501 -1V1C878.785 1 885.501 7.71573 885.501 16H887.501ZM887.501 218V16H885.501V218H887.501ZM870.501 235C879.89 235 887.501 227.389 887.501 218H885.501C885.501 226.284 878.785 233 870.501 233V235ZM612.501 235H870.501V233H612.501V235ZM595.501 218C595.501 227.389 603.112 235 612.501 235V233C604.217 233 597.501 226.284 597.501 218H595.501ZM590.501 211H586.5V213H590.501V211ZM564.5 235C573.889 235 581.5 227.389 581.5 218H579.5C579.5 226.284 572.784 233 564.5 233V235ZM16.5 235H564.5V233H16.5V235ZM-0.5 218C-0.5 227.389 7.11117 235 16.5 235V233C8.21574 233 1.5 226.284 1.5 218H-0.5ZM-0.5 16V218H1.5V16H-0.5ZM586.5 211C582.634 211 579.5 214.134 579.5 218H581.5C581.5 215.239 583.739 213 586.5 213V211ZM590.501 23C594.367 23 597.501 19.866 597.501 16H595.501C595.501 18.7614 593.262 21 590.501 21V23ZM597.501 218C597.501 214.134 594.367 211 590.501 211V213C593.262 213 595.501 215.239 595.501 218H597.501ZM579.5 16C579.5 19.866 582.634 23 586.5 23V21C583.739 21 581.5 18.7614 581.5 16H579.5Z' />
					</clipPath>
				</svg>
				<svg style={{ position: 'absolute', top: 0, left: 0 }}>
					<clipPath id='ticket-background-path'>
						<path d='M0.5 16C0.5 7.16344 7.66344 0 16.5 0H564.5C573.337 0 580.5 7.16344 580.5 16C580.5 19.3137 583.186 22 586.5 22H590.5C593.814 22 596.5 19.3137 596.5 16C596.5 7.16344 603.663 0 612.5 0H870.5C879.337 0 886.5 7.16344 886.5 16V218C886.5 226.837 879.337 234 870.5 234H612.5C603.663 234 596.5 226.837 596.5 218C596.5 214.686 593.814 212 590.5 212H586.5C583.186 212 580.5 214.686 580.5 218C580.5 226.837 573.337 234 564.5 234H16.5C7.66345 234 0.5 226.837 0.5 218V16Z' />
					</clipPath>
				</svg>
			</Box>
		</Box>
	);
};
